import { light, dark, type Palette, type ThemeName } from './tokens.js';

/**
 * Colour crosses into CSS exactly once, as custom properties.
 *
 * Every component styles itself with `var(--w-...)`, so a component never
 * knows which theme it is in and dark mode is not a second set of utilities
 * to keep in step with the first. The workspace's own accent (F-015) overrides
 * `--w-bronze` at runtime, which is why the accent is a variable rather than a
 * compiled value.
 */

export const CSS_VARIABLE_PREFIX = '--w-';

/** Flattens a palette into the custom properties the stylesheet declares. */
export function themeVariables(palette: Palette): Record<string, string> {
  return {
    canvas: palette.canvas,
    surface: palette.surface,
    sunken: palette.sunken,
    plate: palette.plate,
    rule: palette.rule,
    'rule-strong': palette.ruleStrong,

    'ink-900': palette.ink[900],
    'ink-600': palette.ink[600],
    'ink-400': palette.ink[400],
    'ink-300': palette.ink[300],
    'ink-200': palette.ink[200],
    'ink-meta': palette.ink.meta,
    'ink-label': palette.ink.label,

    bronze: palette.bronze.base,
    'bronze-hover': palette.bronze.hover,
    'bronze-tint': palette.bronze.tint,
    'bronze-rule': palette.bronze.rule,
    'bronze-text': palette.bronze.text,
    'bronze-on': palette.bronze.on,

    'mark-success': palette.mark.success,
    'mark-warning': palette.mark.warning,
    'mark-danger': palette.mark.danger,
    'mark-info': palette.mark.info,

    'tint-success': palette.semanticTint.success,
    'tint-warning': palette.semanticTint.warning,
    'tint-danger': palette.semanticTint.danger,
    'tint-info': palette.semanticTint.info,

    'text-success': palette.semanticText.success,
    'text-warning': palette.semanticText.warning,
    'text-danger': palette.semanticText.danger,
    'text-info': palette.semanticText.info,

    'border-control': palette.borderControl,
    focus: palette.focusRing,
    'focus-halo': palette.focusHalo,

    'shadow-popover': palette.elevation.popover,
    'shadow-modal': palette.elevation.modal,
  };
}

/** The custom-property names, so the Tailwind preset can refer to them. */
export const cssVar = (name: keyof ReturnType<typeof themeVariables>): string =>
  `var(${CSS_VARIABLE_PREFIX}${name})`;

function block(selector: string, palette: Palette, indent = '  '): string {
  const lines = Object.entries(themeVariables(palette)).map(
    ([name, value]) => `${indent}${CSS_VARIABLE_PREFIX}${name}: ${value};`,
  );
  return `${selector} {\n${lines.join('\n')}\n}`;
}

/**
 * The stylesheet.
 *
 * Three selectors, in this order, so the three states a viewer can be in all
 * resolve: an explicit light choice, an explicit dark choice, and no choice at
 * all - which follows the operating system. `:root:not([data-theme='light'])`
 * guards the media query so an explicit light choice wins over a dark system.
 */
export function themeCss(): string {
  return [
    '/* GENERATED FILE - do not edit. Produced from packages/ui/src/tokens.ts. */',
    '/* Run `pnpm generate` to regenerate. */',
    '',
    block(':root', light),
    '',
    `@media (prefers-color-scheme: dark) {\n${block("  :root:not([data-theme='light'])", dark, '    ')}\n}`,
    '',
    block("[data-theme='dark']", dark),
    '',
  ].join('\n');
}

/** The themes a person can choose, plus following the operating system. */
export const THEME_CHOICES = ['light', 'dark', 'system'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): ThemeName {
  if (choice === 'system') return systemPrefersDark ? 'dark' : 'light';
  return choice;
}
